import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { cMe, cMyChannels, cNotifications, cSignin, cSignup, cUnreadMessages,
  cUnreadNotifications,
} from '../api';
import { alertsAllowed, registerAlertsWorker, showBrowserAlert } from '../utils/browserAlerts';
import { playSfx } from '../utils/sfx';

/**
 * Mini Shop Community session + live badges.
 *
 * The community has its own account (separate from the per-shop customer
 * accounts) and its own JWT in localStorage (`ms_community_token`).
 * Badges are polled every 15s — when a new notification arrives a toast pops
 * up, which gives the "real notification" feel without a websocket server.
 */
const CommunityContext = createContext(null);

const USER_KEY = 'ms_community_user';
const TOKEN_KEY = 'ms_community_token';
// The channel the member switched their account to (`0` = their own account).
const ACTING_KEY = 'ms_acting_channel';

/** Small bilingual strings for the browser alerts (the context sits outside i18n). */
const ALERTS = {
  kh: {
    ready: 'ការជូនដំណឹងក្នុងកម្មវិធីរុករកបានបើក ✔',
    newNotification: 'ការជូនដំណឹងថ្មី',
    newMessage: 'សារថ្មី',
    open: 'ចុចដើម្បីបើក',
    blocked: 'កម្មវិធីរុករកបានទប់ស្កាត់ការជូនដំណឹង',
  },
  en: {
    ready: 'Browser alerts are on ✔',
    newNotification: 'New notification',
    newMessage: 'New message',
    open: 'Tap to open',
    blocked: 'The browser blocked notifications for this site',
  },
};
const alertsText = () =>
  ALERTS[(localStorage.getItem('ms_lang') || 'kh') === 'kh' ? 'kh' : 'en'];

export function CommunityProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [unread, setUnread] = useState({ notifications: 0, messages: 0 });
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [authReason, setAuthReason] = useState('');
  const [settings, setSettings] = useState({});
  // Browser-notification permission ('granted' | 'denied' | 'default' | 'unsupported')
  const [alerts, setAlerts] = useState(() => (
    typeof window !== 'undefined' && 'Notification' in window
      ? window.Notification.permission
      : 'unsupported'
  ));
  const firstPoll = useRef(true);

  // ── the account switcher: my channels + the one I am acting as ────────────
  // Like a Facebook page: switch from your personal account to a channel and
  // everything you write then belongs to the channel, not to you.
  const [channels, setChannels] = useState([]);
  const [actingId, setActingId] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(ACTING_KEY) || 0);
      return Number.isFinite(saved) ? saved : 0;
    } catch (e) { return 0; }
  });

  /** My channels (max 3) — refreshed after login and after creating one. */
  const refreshChannels = useCallback(() => {
    let token = null;
    try { token = localStorage.getItem(TOKEN_KEY); } catch (e) { token = null; }
    if (!token) { setChannels([]); return Promise.resolve([]); }
    return cMyChannels()
      .then((res) => {
        const items = res?.items || [];
        setChannels(items);
        return items;
      })
      .catch(() => []);
  }, []);

  useEffect(() => { refreshChannels(); }, [refreshChannels, token]);

  /** Switch the “posting as” account: `0` = me, otherwise a channel id. */
  const setActingChannel = useCallback((id) => {
    const value = Number(id) || 0;
    setActingId(value);
    try {
      if (value) localStorage.setItem(ACTING_KEY, String(value));
      else localStorage.removeItem(ACTING_KEY);
    } catch (e) { /* private mode — the choice lives for this visit only */ }
  }, []);

  // A channel that was deleted (or no longer mine) falls back to my account.
  useEffect(() => {
    if (!actingId) return;
    if (channels.length && !channels.some((c) => c.id === actingId)) setActingChannel(0);
  }, [channels, actingId, setActingChannel]);

  const actingChannel = channels.find((c) => c.id === actingId) || null;

  /**
   * Show a desktop / phone-browser alert (silently ignored when not allowed).
   * Goes through the service worker so the alert survives closing the tab and
   * opens the article when it is tapped — see `utils/browserAlerts`.
   */
  const pushBrowserAlert = useCallback((title, body = '', url = '/') => {
    showBrowserAlert({ title, body, url, tag: 'ckl-community' });
  }, []);

  // The service worker is what makes “closed the tab but still get the alert”
  // possible — register it as soon as notifications are allowed.
  useEffect(() => {
    if (alerts === 'granted') registerAlertsWorker();
  }, [alerts]);

  /** Ask the browser for notification permission (called from a button). */
  const enableAlerts = useCallback(async () => {
    const txt = alertsText();
    if (typeof window === 'undefined' || !('Notification' in window)) {
      toast.error(txt.blocked);
      return 'unsupported';
    }
    let perm = window.Notification.permission;
    try { perm = await window.Notification.requestPermission(); } catch (e) { perm = 'denied'; }
    setAlerts(perm);
    if (perm === 'granted') {
      pushBrowserAlert('CodeKhmerLearning', txt.ready);
      toast.success(txt.ready);
    } else {
      toast.error(txt.blocked);
    }
    return perm;
  }, [pushBrowserAlert]);

  const persistUser = useCallback((u) => {
    setUser(u);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  }, []);

  const setSession = useCallback((res) => {
    localStorage.setItem(TOKEN_KEY, res.access_token);
    persistUser(res.user);
    setToken(res.access_token);
    return res.user;
  }, [persistUser]);

  const signin = useCallback(async (identifier, password) => {
    const res = await cSignin({ identifier, password });
    setAuthOpen(false);
    toast.success(res.user?.name || res.user?.username || '');
    return setSession(res);
  }, [setSession]);

  const signup = useCallback(async (payload) => {
    const res = await cSignup(payload);
    setAuthOpen(false);
    toast.success('🎉');
    return setSession(res);
  }, [setSession]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    persistUser(null);
    setToken(null);
    setUnread({ notifications: 0, messages: 0 });
    toast.success('👋');
  }, [persistUser]);

  const refresh = useCallback(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    return cMe()
      .then((u) => {
        persistUser(u);
        setUnread({ notifications: u.unread_notifications || 0, messages: u.unread_messages || 0 });
        return u;
      })
      .catch(() => {});
  }, [persistUser]);

  const requireAuth = useCallback((reason = '') => {
    if (localStorage.getItem(TOKEN_KEY)) return true;
    setAuthReason(reason);
    setAuthMode('signin');
    setAuthOpen(true);
    return false;
  }, []);

  const openAuth = (mode = 'signin', reason = '') => {
    setAuthMode(mode);
    setAuthReason(reason);
    setAuthOpen(true);
  };

  // Refresh the profile after login / page load.
  useEffect(() => { if (token) refresh(); }, [token, refresh]);

  // Poll unread counters (notifications + chat).
  //   · tab visible → in-app toast + chime
  //   · tab hidden  → a real browser notification (it stays on screen even after
  //                   the tab is closed, and opens the article when tapped)
  //   · fresh start → “while you were away” summary for notifications we never
  //                   alerted about yet (browser closed, phone locked, …)
  const lastCounts = useRef({ notifications: 0, messages: 0 });
  useEffect(() => {
    if (!token) return undefined;
    let stopped = false;

    /** Newest notification (or null): gives the alert text and its link. */
    const newest = () => cNotifications({ limit: 1 })
      .then((res) => res?.items?.[0] || null)
      .catch(() => null);

    /** Remember the newest notification the member was already told about. */
    const remember = (item) => {
      if (item && item.id) {
        try { localStorage.setItem('ms_last_notif_id', String(item.id)); } catch (e) { /* ignore */ }
      }
    };

    const tick = async () => {
      if (stopped) return;
      const txt = alertsText();
      try {
        const [n, m] = await Promise.all([cUnreadNotifications(), cUnreadMessages()]);
        const found = n.unread || 0;
        const msgs = m.unread || 0;
        const before = lastCounts.current;
        lastCounts.current = { notifications: found, messages: msgs };
        setUnread({ notifications: found, messages: msgs });

        if (firstPoll.current) {
          // First poll of this visit — anything that arrived while the browser
          // was closed (or the phone was locked) is announced once.
          let seen = 0;
          try { seen = Number(localStorage.getItem('ms_last_notif_id') || 0); } catch (e) { seen = 0; }
          if (found > 0) {
            const item = await newest();
            remember(item);
            if (alertsAllowed() && item && item.id > seen) {
              showBrowserAlert({
                title: 'CodeKhmerLearning',
                body: found > 1
                  ? `${found} 🔔 · ${item.text || txt.newNotification}`
                  : (item.text || txt.newNotification),
                url: item.post_id ? `/post/${item.post_id}` : '/notifications',
                tag: 'ckl-while-away',
              });
            }
          }
        } else {
          if (found > before.notifications && found > 0) {
            playSfx('notify');                       // new notification → gentle chime
            const item = await newest();
            remember(item);
            const message = item?.text || txt.newNotification;
            const url = item?.post_id ? `/post/${item.post_id}` : '/notifications';
            if (document.hidden) pushBrowserAlert('CodeKhmerLearning', message, url);
            else toast(message, { icon: '🔔', id: 'community-toast', duration: 4000 });
          }
          if (msgs > before.messages && msgs > 0) {
            playSfx('notify');                       // new chat message
            if (document.hidden) {
              pushBrowserAlert('CodeKhmerLearning', `${txt.newMessage} · ${txt.open}`, '/messages');
            }
          }
        }
      } catch (e) { /* offline — keep the last counters */ }
      firstPoll.current = false;
    };

    firstPoll.current = true;
    tick();
    const iv = setInterval(tick, 15000);
    return () => { stopped = true; clearInterval(iv); };
  }, [token, pushBrowserAlert]);

  const value = {
    user, token,
    isLoggedIn: !!token,
    unread,
    authOpen, authMode, authReason,
    openAuth, closeAuth: () => setAuthOpen(false),
    setAuthMode,
    signin, signup, logout, refresh, requireAuth,
    setSession,
    alerts, enableAlerts, pushBrowserAlert,
    settings, setSettings,
    isOnline: (id) => !!id,
    // the account switcher (post / comment as a channel)
    channels, refreshChannels, actingChannel, setActingChannel,
    isActingAsChannel: !!actingChannel,
  };

  return <CommunityContext.Provider value={value}>{children}</CommunityContext.Provider>;
}

export const useCommunity = () => useContext(CommunityContext);
